import { Box, Image as ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';
import { FilePreview, type FilePreviewItem } from './file-preview';
import { ProductImage, type DisplayCrop } from './product-image';

interface ImageItem {
	id: string;
	url?: string | null;
	name: string;
	isPrimary?: boolean | null;
	crop?: DisplayCrop | null;
}

interface ImageViewerProps {
	images: ImageItem[];
	initialIndex?: number;
}

export function ImageViewer({ images, initialIndex = 0 }: ImageViewerProps) {
	const sorted = [...images].sort((a, b) => (a.isPrimary ? -1 : b.isPrimary ? 1 : 0));
	const [activeIdx, setActiveIdx] = useState(initialIndex);
	const [previewOpen, setPreviewOpen] = useState(false);
	const active = sorted[activeIdx];

	const previewFiles: FilePreviewItem[] = sorted
		.filter((img) => img.url)
		.map((img) => ({ id: img.id, url: img.url!, name: img.name, mimeType: 'image/jpeg' }));

	if (!sorted.length) {
		return (
			<div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-[var(--r-3)] border border-line-1 bg-surface-fill">
				<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface-input text-text-4">
					<Box size={18} />
				</div>
				<span className="text-xs text-text-4">No image</span>
			</div>
		);
	}

	return (
		<>
			<div>
				{/* Main image */}
				<div
					className="group mb-3 cursor-pointer overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-fill"
					onClick={() => setPreviewOpen(true)}
				>
					{active?.url ? (
						<ProductImage
							image={active}
							alt={active.name}
							className="aspect-square w-full transition-transform duration-300 group-hover:scale-[1.02]"
						/>
					) : (
						<div className="flex aspect-square flex-col items-center justify-center gap-2">
							<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface-input text-text-4">
								<Box size={18} />
							</div>
							<span className="text-xs text-text-4">No image</span>
						</div>
					)}
				</div>

				{/* Thumbnail strip */}
				{sorted.length > 1 && (
					<div className="flex gap-2 overflow-x-auto">
						{sorted.map((img, i) => (
							<button
								key={img.id}
								onClick={() => setActiveIdx(i)}
								className={cn(
									'h-14 w-14 shrink-0 cursor-pointer overflow-hidden rounded-[var(--r-2)] border-2 transition-colors',
									i === activeIdx
										? 'border-accent'
										: 'border-line-1 hover:border-line-2'
								)}
							>
								{img.url ? (
									<img
										src={img.url}
										alt={img.name}
										className="h-full w-full object-cover"
									/>
								) : (
									<div className="flex h-full w-full items-center justify-center bg-surface-fill">
										<ImageIcon size={12} className="text-text-4" />
									</div>
								)}
							</button>
						))}
					</div>
				)}
			</div>

			{previewOpen && previewFiles.length > 0 && (
				<FilePreview
					files={previewFiles}
					initialIndex={activeIdx}
					onClose={() => setPreviewOpen(false)}
				/>
			)}
		</>
	);
}
